import React from 'react';

import { css } from '@emotion/css';
import { Form } from 'antd';
import { theme } from 'theme';

import { responsiveFormLayout } from 'components/FormItems';
import { Input } from 'components/Input';
import { TextArea } from 'components/TextArea';
import { Typography } from 'components/Typography';

export interface CustomRuleRequestPanelProps {
  children: React.ReactNode;
  showCustomRuleRequestPanel: boolean;
  customRuleRequestData: {
    ruleName: string;
    businessProblem: string;
    triggerConditions: string;
    additionalRequirements: string;
  };
  onCustomRuleRequestDataChange: (field: string, value: string) => void;
}

export const CustomRuleRequestPanel = ({
  children,
  showCustomRuleRequestPanel: showCustomRulePanel,
  customRuleRequestData: customRuleData,
  onCustomRuleRequestDataChange,
}: CustomRuleRequestPanelProps) => (
  <div
    className={css`
      width: 200%;
      display: flex;
      transition: transform 0.3s ease-in-out;
      transform: translateX(${showCustomRulePanel ? '-50%' : '0'});
      overflow-x: hidden;
      overflow-y: visible;
      height: 100%;
      max-height: calc(100vh - 200px);
    `}
  >
    {/* Main Panel */}
    <div
      className={css`
        width: 50%;
        flex-shrink: 0;
        overflow-y: auto;
        overflow-x: hidden;
        box-sizing: border-box;
      `}
    >
      {children}
    </div>

    {/* Custom Rule Request Form */}
    <div
      className={css`
        width: 50%;
        flex-shrink: 0;
        padding: 24px 40px;
        overflow-y: auto;
        overflow-x: hidden;
        box-sizing: border-box;
      `}
    >
      <Form layout="horizontal" labelAlign="left" {...responsiveFormLayout}>
        <Typography type="subtitle" style={{ color: theme.colors.neutral[600], marginBottom: 24 }}>
          Describe the custom validation rule you need and we'll build it for you.
        </Typography>

        {/* Rule Name */}
        <Form.Item name="ruleName" label="Rule Name" rules={[{ required: true, message: 'Rule name is required' }]}>
          <Input
            name="ruleName-input"
            placeholder="Enter a name for your custom rule"
            value={customRuleData.ruleName}
            onChange={(e) => onCustomRuleRequestDataChange('ruleName', e.target.value)}
          />
        </Form.Item>

        {/* Rule Purpose */}
        <Form.Item
          name="businessProblem"
          label="Rule Purpose"
          rules={[{ required: true, message: 'Rule purpose is required' }]}
        >
          <Input
            name="rulePurpose-input"
            placeholder="What will this rule help you address?"
            value={customRuleData.businessProblem}
            onChange={(e) => onCustomRuleRequestDataChange('businessProblem', e.target.value)}
          />
        </Form.Item>

        {/* Trigger Conditions */}
        <Form.Item
          name="triggerConditions"
          label="Trigger conditions"
          rules={[{ required: true, message: 'Trigger conditions are required' }]}
        >
          <TextArea
            name="triggerConditions-input"
            placeholder={`What inventory should be highlighted with this rule?\nBe as descriptive as possible - this will help the Recurrency team ensure your rule precisely targets the most relevant inventory.`}
            value={customRuleData.triggerConditions}
            onChange={(e) => onCustomRuleRequestDataChange('triggerConditions', e.target.value)}
            rows={4}
          />
        </Form.Item>

        {/* Additional Requirements */}
        <Form.Item name="additionalRequirements" label="Additional Requirements">
          <TextArea
            name="additionalRequirements-input"
            placeholder="Any extra information you provide here may help the Recurrency team create your rule with fewer follow-ups or iterations"
            value={customRuleData.additionalRequirements}
            onChange={(e) => onCustomRuleRequestDataChange('additionalRequirements', e.target.value)}
            rows={4}
          />
        </Form.Item>
      </Form>
    </div>
  </div>
);
